import React, { useEffect, useState } from 'react';
// Design system first: tokens, reset and @layer order must be established
// before any component stylesheet loads. Previously imported inside
// Dashboard.jsx, which meant the load order depended on which component
// rendered first, and the login screen pulled it in only incidentally.
import './components/globals.css';
import Dashboard from './components/Dashboard';
import LoginPage from './components/LoginPage';
import PendingApprovalPage from './components/PendingApprovalPage';
import AcceptTermsPage from './components/AcceptTermsPage';
import LegalPage from './legal/LegalPage';
import useTermsStatus from './legal/useTermsStatus';
import { legalDocFromHash } from './legal/legalConfig';
import { AuthProvider, useAuth } from './context/AuthContext';
import { UserProfileProvider } from './context/UserProfileContext';

// Inner app -- only renders when authenticated
function AuthGate() {
  const { isAuthenticated, isLoading, pendingApproval, approvalPending } = useAuth();
  const terms = useTermsStatus(isAuthenticated);

  // While checking stored session, show nothing (avoids flash)
  if (isLoading) return null;

  if (!isAuthenticated) return <LoginPage />;

  // Terms before anything else a signed-in user sees — including the pending screen, so
  // a new sign-up accepts at sign-up rather than whenever they are approved. Fails
  // closed: 'error' renders AcceptTermsPage's retry, never the app. See useTermsStatus.
  if (terms.state === 'loading') return null;
  if (terms.state !== 'accepted') return <AcceptTermsPage status={terms} />;

  // Signed in, but we do not yet know whether they are approved. Rendering the dashboard
  // here is what caused a visible flash of the command centre before the pending screen
  // replaced it — and it is not only cosmetic: the dashboard mounts, fires every one of
  // its API calls, and collects a wall of 401s on behalf of a user who has no access.
  // Waiting costs one round trip, and only for users whose claim suggests they may be
  // pending.
  if (approvalPending) return null;

  // Signed in, but no tenant claim — the account exists and is awaiting approval.
  // Rendering the dashboard here would show every page empty with no explanation,
  // which reads as a broken product rather than a queue. See PendingApprovalPage.
  if (pendingApproval) return <PendingApprovalPage />;

  return (
    <UserProfileProvider>
      <Dashboard />
    </UserProfileProvider>
  );
}

// #privacy / #terms, tracked across hash changes. Links between the two documents are
// plain hash links, so this has to follow them rather than read the hash once.
function useLegalDoc() {
  const [doc, setDoc] = useState(() => legalDocFromHash(window.location.hash));
  useEffect(() => {
    const onHash = () => setDoc(legalDocFromHash(window.location.hash));
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);
  return doc;
}

// Root -- AuthProvider wraps everything
function App() {
  // The legal pages come before auth: they must be readable by someone deciding whether
  // to sign up, and by someone sitting at the acceptance screen.
  const legalDoc = useLegalDoc();
  if (legalDoc) return <LegalPage doc={legalDoc} />;

  return (
    <AuthProvider>
      <AuthGate />
    </AuthProvider>
  );
}

export default App;
